// Le fil de discussion d'un ticket : commentaires consécutifs d'un même auteur regroupés (l'avatar
// n'apparaît qu'une fois par groupe), séparateurs de jour (Aujourd'hui / Hier / date), et les
// messages de l'utilisateur courant alignés à droite avec un ton marque distinct.
// `buildThreadItems` / `dayLabelFr` sont pures et exportées pour être testables.
import React from "react";
import { Link } from "react-router-dom";
import { parseApiDate } from "../../Helper/dates";
import { UserAvatar } from "../../Components/UserAvatar";
import { RelativeTime } from "./TicketBits";
import type { TicketAuthor, TicketComment } from "./ticketsClient";

export type ThreadItem =
	| { kind: "day"; key: string; label: string }
	| {
			kind: "group";
			key: string;
			author?: TicketAuthor | null;
			authorName: string;
			/** Le groupe appartient à l'utilisateur courant → aligné à droite, ton marque. */
			own: boolean;
			comments: TicketComment[];
	  };

/** Clé de jour LOCALE (yyyy-mm-dd) : le regroupement suit le fuseau de l'opérateur. */
const localDayKey = (d: Date): string =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** « Aujourd'hui », « Hier », sinon la date française longue (année seulement si différente). */
export const dayLabelFr = (date: Date, now: Date = new Date()): string => {
	const key = localDayKey(date);
	if (key === localDayKey(now)) return "Aujourd'hui";
	const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
	if (key === localDayKey(yesterday)) return "Hier";
	const sameYear = date.getFullYear() === now.getFullYear();
	return date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", ...(sameYear ? {} : { year: "numeric" }) });
};

/** E-mail d'un commentaire, en minuscules (résolu ou brut), pour comparer les auteurs. */
const commentEmail = (c: TicketComment): string => (c.author?.email || c.authorEmail || "").toLowerCase();

/**
 * Transforme la liste plate de commentaires (ordre chronologique de l'API) en items de fil :
 * un séparateur à chaque changement de jour, un groupe à chaque changement d'auteur (ou de jour).
 */
export const buildThreadItems = (comments: TicketComment[], currentUserEmail?: string | null, now: Date = new Date()): ThreadItem[] => {
	const me = (currentUserEmail ?? "").trim().toLowerCase();
	const items: ThreadItem[] = [];
	let lastDayKey: string | null = null;
	let lastGroup: Extract<ThreadItem, { kind: "group" }> | null = null;

	for (const c of comments) {
		const date = parseApiDate(c.createdAt);
		const dayKey = date ? localDayKey(date) : "?";
		if (dayKey !== lastDayKey) {
			items.push({ kind: "day", key: `day-${dayKey}-${items.length}`, label: date ? dayLabelFr(date, now) : "-" });
			lastDayKey = dayKey;
			lastGroup = null; // un nouveau jour recommence toujours un groupe
		}
		const email = commentEmail(c);
		if (lastGroup && commentEmail(lastGroup.comments[lastGroup.comments.length - 1]) === email) {
			lastGroup.comments.push(c);
		} else {
			lastGroup = {
				kind: "group",
				key: `group-${c.id}`,
				author: c.author ?? null,
				authorName: c.author?.name || c.authorEmail || "Système",
				own: !!me && email === me,
				comments: [c]
			};
			items.push(lastGroup);
		}
	}
	return items;
};

/** Un groupe de bulles : avatar une seule fois, nom + heure sur la première bulle. */
const ThreadGroup: React.FC<{ group: Extract<ThreadItem, { kind: "group" }> }> = ({ group }) => {
	const avatar = (
		<UserAvatar
			name={group.authorName}
			id={group.author?.id ?? undefined}
			avatarKey={group.author?.avatarKey}
			pictureUrl={group.author?.pictureUrl}
			sizeClass="mt-0.5 h-7 w-7"
			textClass="text-[9px]"
			title={group.authorName}
		/>
	);
	return (
		<li className={`flex items-start gap-2.5 ${group.own ? "flex-row-reverse" : ""}`}>
			{avatar}
			<div className={`flex min-w-0 max-w-[88%] flex-1 flex-col gap-1 ${group.own ? "items-end" : "items-start"}`}>
				<div className={`flex flex-wrap items-baseline gap-x-2 ${group.own ? "flex-row-reverse" : ""}`}>
					{group.author?.id ? (
						<Link to={`/account/${group.author.id}`} className={`text-xs font-semibold transition hover:underline ${group.own ? "text-[#005CA9] hover:text-[#004a87]" : "text-slate-700 hover:text-sky-700"}`}>
							{group.own ? `${group.authorName} (vous)` : group.authorName}
						</Link>
					) : (
						<span className={`text-xs font-semibold ${group.own ? "text-[#005CA9]" : "text-slate-700"}`}>
							{group.own ? `${group.authorName} (vous)` : group.authorName}
						</span>
					)}
					<RelativeTime value={group.comments[0].createdAt} className="text-[11px] text-slate-400" />
				</div>
				{group.comments.map((c, i) => (
					<div
						key={c.id}
						className={`w-fit max-w-full rounded-2xl border px-3.5 py-2 ${
							group.own
								? `border-[#005CA9]/20 bg-[#005CA9]/[0.06] ${i === 0 ? "rounded-tr-md" : ""}`
								: `border-slate-100 bg-slate-50 ${i === 0 ? "rounded-tl-md" : ""}`
						}`}
					>
						<p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{c.body}</p>
						{i > 0 && <RelativeTime value={c.createdAt} className="mt-0.5 block text-right text-[10px] text-slate-300" />}
					</div>
				))}
			</div>
		</li>
	);
};

export const TicketThread: React.FC<{ comments: TicketComment[]; currentUserEmail?: string | null }> = ({ comments, currentUserEmail }) => {
	if (comments.length === 0) {
		return <p className="text-sm text-slate-400">Aucun commentaire : lancez la discussion ci-dessous.</p>;
	}
	const items = buildThreadItems(comments, currentUserEmail);
	return (
		<ol className="space-y-3">
			{items.map((item) =>
				item.kind === "day" ? (
					<li key={item.key} className="flex items-center gap-3" aria-label={item.label}>
						<span className="h-px flex-1 bg-slate-100" aria-hidden />
						<span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
							{item.label}
						</span>
						<span className="h-px flex-1 bg-slate-100" aria-hidden />
					</li>
				) : (
					<ThreadGroup key={item.key} group={item} />
				)
			)}
		</ol>
	);
};

export default TicketThread;
